import React from 'react';
import clsx from 'clsx';
import { FlamingoFund, UnitCategoryCode, FundDocumentType } from 'funds-model';
import Decimal from 'decimal.js';
import FundListItemIcons from '../FundList/FundListItemIcons';
import { toHashColorHexString } from '../utils/colorUtils';
import { useAutoFocus } from '../utils/useAutoFocus';
import { Title } from '../Title/Title';
import { Typography } from '../Typography/Typography';
import { useIsMobile } from '../utils/useIsMobile';
import GoBackButton from '../GoBackButton/GoBackButton';
import { useTranslation } from 'react-i18next';
import { Link } from '../Link/Link';
import useStyles from './FundDetailsDialog.style';

export interface FundDetailsDialogHeaderProps {
  fund: FlamingoFund;
  closeHandler: () => void;
  fundValue?: Decimal;
  backgroundImage?: string;
  backgroundImageType?: string;
  isFundInadequate?: boolean;
  className?: string;
  processLayout?: boolean;
  hasSidebarNavbar?: boolean;
  unitCategoryCode: UnitCategoryCode;
}

const FundDetailsDialogHeader: React.FC<FundDetailsDialogHeaderProps> = ({
  fund,
  closeHandler,
  fundValue,
  backgroundImage,
  backgroundImageType,
  isFundInadequate,
  className,
  processLayout = false,
  hasSidebarNavbar = !processLayout,
  unitCategoryCode
}) => {
  const autoFocusRef = useAutoFocus<HTMLButtonElement>();
  const c = useStyles({
    titleLength: fund.name.length + (fund.previousName?.length || 0),
    processLayout,
    hasSidebarNavbar
  });
  const isMobile = useIsMobile();
  const [t] = useTranslation('components');
  const [tt] = useTranslation();
  const documentsForUnitCode = fund.documentsForUnitCode[unitCategoryCode];
  const documentPdf = documentsForUnitCode?.find(doc => doc.typeCode === FundDocumentType.KARTY_FUNDUSZY);
  return (
    <div className={clsx(c.backgroundImageWrapper, className)}>
      <div className={c.backgroundImage} style={{ backgroundImage: `url(data:${backgroundImageType};base64,${backgroundImage})` }}>
        <div className={c.backgroundImageOverlay}>
          <header className={c.header}>
            <div className={c.headerContent}>
              <GoBackButton
                label={tt('back')}
                onClick={closeHandler}
                ref={autoFocusRef}
              />
              <FundListItemIcons
                fundValue={fundValue}
                fullNameUmbrella={!isMobile}
                fund={fund}
                isFundInadequate={!!isFundInadequate}
                umbrellaColor={toHashColorHexString(fund?.umbrella?.colorHex)}
                className={c.fundListItemIcons}
              />
              <Title
                titleVariant={isMobile ? 'h3' : 'h2'}
                title={fund.name}
                titleFontWeight="extraBold"
                className={c.fundNameTitle}
              />
              <Typography
                component="p"
                variant="body2"
                className={c.previousName}
              >
                {fund.previousName}
              </Typography>
              {documentPdf && <Link
                className={c.downloadFundCardLink}
                as="link"
                href={`${documentPdf.url}&unitCategoryId=${unitCategoryCode}`}
                variant="underline"
                showDocumentIcon
              >
                {t('fund-details-dialog.download-fund-card')}
              </Link>}

            </div>
          </header>
        </div>
      </div>
    </div>
  );
};

export default FundDetailsDialogHeader;
